/**
 * Product-type labels for order surfaces and order tools (Sheila, 2026-10-22): every row reads
 * as what the product IS — Meal, Main, Side, Condiment, Milk… A Meal is a
 * composed bundle and nothing else; "Entrée" is not a category. A school-added
 * food sitting in a meal's column keeps its own type: a syrup cup next to the
 * pancakes is a Condiment, never a Meal. Grouping it beside its bundle is a separate, positional concern.
 */

export interface ProductTypeItem {
  itemKind: 'BUNDLE' | 'COMPONENT'
  component?: { kind?: string | null } | null
  lineupMilkOptionCode?: string | null
}

const COMPONENT_TYPE_LABELS: Readonly<Record<string, string>> = {
  MAIN_ENTREE: 'Main',
  SIDE: 'Side',
  FRUIT: 'Fruit',
  VEGETABLE: 'Vegetable',
  GRAIN: 'Grain',
  BEVERAGE: 'Beverage',
  CONDIMENT: 'Condiment',
  SUPPLEMENT: 'Supplement',
}

/** Display order of the type labels, bundles first. */
export const PRODUCT_TYPE_ORDER: readonly string[] = [
  'Meal',
  'Main',
  'Side',
  'Fruit',
  'Vegetable',
  'Grain',
  'Condiment',
  'Supplement',
  'Milk',
  'Beverage',
  'Item',
]

/** The row's product type: "Meal" for a bundle, the component's kind, "Milk" for system milk, or "Item" when unknown. */
export function productTypeLabel(item: ProductTypeItem): string {
  if (item.itemKind === 'BUNDLE') {
    return 'Meal'
  }
  // System milk has its own line even though it rides with the bundle.
  if (item.lineupMilkOptionCode) {
    return 'Milk'
  }
  const kind = item.component?.kind
  return (kind && COMPONENT_TYPE_LABELS[kind]) || 'Item'
}

/** Top-level sale category: a meal, or a product sold on its own. */
export type SaleCategory = 'Meal' | 'A La Carte'

/**
 * Meal or A La Carte (Sheila, 2026-10-22). A bundle is the meal; system milk
 * rides with it, so it counts with the meal. Any other product on its own line
 * — an entrée, side, condiment or drink sold by itself — is A La Carte.
 */
export function saleCategory(item: ProductTypeItem): SaleCategory {
  return item.itemKind === 'BUNDLE' || item.lineupMilkOptionCode ? 'Meal' : 'A La Carte'
}
